import { readShopifyInstallCallbackCorrelation } from './shopifyInstall';

type CompletionOptions = {
    attemptId: string;
    workspaceId: string;
    popup: Window;
    signal?: AbortSignal;
    isCurrent: () => boolean;
    start: () => void | Promise<void>;
    verify: () => Promise<boolean>;
};

// An old inventory record can confirm the shop only AFTER this OAuth attempt
// reports success. Missing callback delivery fails visibly instead of guessing.
export function waitForShopifyInstallCompletion<T extends {blockConnect?: boolean}>(options: CompletionOptions): Promise<T | null> {
    return new Promise((resolve, reject) => {
        const channel = new BroadcastChannel('connect');
        let finished = false;
        let callbackReceived = false;
        let callbackPayload: T | null = null;
        let verificationTimer: ReturnType<typeof setTimeout> | undefined;
        let attempts = 0;
        const recovery = () => new Error("We couldn't finish connecting Shopify. Please try again.");
        const cleanup = () => {
            clearTimeout(deadline);
            clearInterval(watch);
            clearTimeout(verificationTimer);
            channel.onmessage = null;
            channel.close();
            options.signal?.removeEventListener('abort', cancel);
        };
        const finish = (error?: Error, payload: T | null = null) => {
            if (finished) return;
            finished = true;
            cleanup();
            if (error) reject(error); else resolve(payload);
        };
        const cancel = () => finish(recovery());
        const active = () => !finished && !options.signal?.aborted && options.isCurrent();
        const deadline = setTimeout(cancel, 300000);
        const watch = setInterval(() => {
            if (!active() || (!callbackReceived && options.popup.closed)) cancel();
        }, 500);
        const verify = async () => {
            if (!active()) { cancel(); return; }
            try {
                const confirmed = await options.verify();
                if (!active()) { cancel(); return; }
                if (confirmed) { finish(undefined, callbackPayload); return; }
                if (++attempts >= 8) { cancel(); return; }
                verificationTimer = setTimeout(() => void verify(), 1000);
            } catch {
                finish(recovery());
            }
        };
        channel.onmessage = (event) => {
            const message = event.data;
            const correlation = readShopifyInstallCallbackCorrelation(message);
            if (!active()) { cancel(); return; }
            if (callbackReceived || !correlation || message.provider !== 'shopify'
                || correlation.workspaceId !== options.workspaceId || correlation.attemptId !== options.attemptId
                || !['SUCCESS', 'ERROR'].includes(message.type)) return;
            callbackReceived = true;
            callbackPayload = message.payload && typeof message.payload === 'object' ? message.payload as T : null;
            if (message.type === 'ERROR') {
                finish(new Error(typeof message.message === 'string' ? message.message : recovery().message));
            } else if (message.payload?.blockConnect === true) {
                finish(undefined, message.payload as T);
            } else {
                void verify();
            }
        };
        options.signal?.addEventListener('abort', cancel, {once: true});
        if (!active()) { cancel(); return; }
        Promise.resolve().then(() => { if (active()) return options.start(); }).catch(() => finish(recovery()));
    });
}
